import { makeStructure } from "@/app/utils/helpers";
import "./AuthMatrix.scss";
import { useTranslation } from "react-i18next";
import GxIconButton from "@/app/components/common/actions/buttons/GxIconButton";

interface EditDeleteType {
  id: string;
  from: string;
  to: string;
  approvers: string;
  editDelete: string;
  accountId?: number;
  fromAmount?: number;
  toAmount?: number;
  requiredApprovers?: number;
  paymentTypeId?: number;
  currencyCode2?: number;
}

interface AuthMatrixCardProp {
  data: EditDeleteType[];
  setEditDeleteCard: (value: EditDeleteType) => void;
  allowEditAll?: boolean;
  // Used ONLY to decide whether the "to" amount should render as
  // "Max Amount" in this view. Never used to alter item.to/toAmount
  // themselves — every other consumer (cascading, disable checks,
  // save/edit/delete payloads) keeps reading the raw numeric value.
  maxAmount?: string;
}

export const AuthMatrixCard = ({
  data,
  setEditDeleteCard,
  allowEditAll = false,
  maxAmount,
}: AuthMatrixCardProp) => {
  const { t } = useTranslation();

  const handleEdit = (item: EditDeleteType) => {
    setEditDeleteCard(item);
  };

  return (
    <div className="gx-auth-matrix-card-container">
      {data?.map((item, index) => {
        const splittedFromAmount = item.from.split(".");
        const splittedToAmount = item.to.split(".");

        // Display-only check — does NOT touch item.to/toAmount.
        const isToAtMax =
          maxAmount != null &&
          maxAmount !== "" &&
          !Number.isNaN(Number.parseFloat(item.to)) &&
          !Number.isNaN(Number.parseFloat(maxAmount)) &&
          Number.parseFloat(item.to) === Number.parseFloat(maxAmount);

        const isLastItem = data[data.length - 1].id === item.id;

        // Allow editing all cards when allowEditAll=true
        // const canEdit = allowEditAll
        //   ? true
        //   : (isLastItem && data.length > 1) || data.length === 1;

        // Enable edit only for the last card
        const canEdit = data[data.length - 1].id === item.id;

        // Delete only last card
        const canDelete = isLastItem && data.length > 1;

        return (
          <div key={item.id || index} className="gx-auth-matrix-card">
            {/* From & To Amounts */}
            <div className="gx-amount-grid">
              <div className="gx-authmatrix-card-amount ">
                <span className="gx-p12-sb">{t("from_amount")}</span>

                <div className="gx-p12-sb">
                  <span>{item.currencyCode2}</span>
                  <span>{makeStructure(splittedFromAmount[0])}</span>
                  <span>.{splittedFromAmount[1]}</span>
                </div>
              </div>

              <div className="gx-authmatrix-card-amount ">
                <span className="gx-p12-sb">{t("to_amount")}</span>

                <div className="gx-p12-sb">
                  {isToAtMax ? (
                    <span>Max Amount</span>
                  ) : (
                    <>
                      <span>{item.currencyCode2}</span>
                      <span>{makeStructure(splittedToAmount[0])}</span>
                      <span>.{splittedToAmount[1]}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <hr />

            {/* Footer */}
            <div className="gx-footer-row">
              <div className="gx-footer-row-approvers">
                <span className="gx-label gx-p14-sb">
                  {t("required_approvers")}:
                </span>{" "}
                <span className="gx-value gx-p14-sb">{item.approvers}</span>
              </div>

              <div className="gx-footer-row-actions">
                {/* Edit Button */}
                <GxIconButton
                  type="edit"
                  disabled={!canEdit}
                  alt="Edit Icon"
                  onClick={() =>
                    handleEdit({
                      ...item,
                      editDelete: "edit",
                    })
                  }
                />

                {/* Delete Button */}
                <GxIconButton
                  type="delete"
                  disabled={!canDelete}
                  alt="Delete Icon"
                  onClick={() =>
                    handleEdit({
                      ...item,
                      editDelete: "delete",
                    })
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
